import React, { useState, useEffect } from 'react';
import { Station, StationType, Coupon, getAllCoupons } from '../Firebase/firebaseApi.ts';
import CouponSelection from './CouponSelection.tsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  TextField
} from '@mui/material';
import PdfDialog from '../components/PdfDialog.tsx';

// Function to get payment link for a specific slot type
function getPaymentLink(stationType: StationType, slotType: string): string {
  switch (slotType) {
    case 'hour':
      return stationType.payLinks.hour || '';
    case 'day':
      return stationType.payLinks.day || '';
    case 'month':
      return stationType.payLinks.month || '';
    default:
      return '';
  }
}

interface BookingDialogProps {
  isOpen: boolean;
  selectedStation: Station | null;
  selectedType: StationType | null;
  selectedSlotType: string; // The currently selected slot type
  selectedDate: string;
  selectedEndDate: string;
  selectedStartTime: string;
  selectedEndTime: string;
  /** When set (recurring mode), dialog shows series summary and multiplies price */
  seriesDates?: string[];
  /** Recurring dates skipped because the station is already booked */
  skippedConflictDates?: string[];
  userEmail: string;
  userName: string;
  payLoading: boolean;
  payError: string | null;
  paySuccess: string | null;
  onClose: () => void;
  onPay: (phoneNumber: string) => void;
  /** When true, תקנון acceptance is not required (admin booking flow). */
  isAdmin?: boolean;
}


function calculateDuration(selectedSlotType: string, selectedDate: string, selectedEndDate: string, selectedStartTime: string, selectedEndTime: string): number {
  if (selectedSlotType === 'hour' && selectedStartTime && selectedEndTime) {
    const startHour = parseInt(selectedStartTime.split(':')[0]);
    const startMinute = parseInt(selectedStartTime.split(':')[1]);
    const endHour = parseInt(selectedEndTime.split(':')[0]);
    const endMinute = parseInt(selectedEndTime.split(':')[1]);
    
    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;
    
    const durationMinutes = endTotalMinutes - startTotalMinutes;
    return Math.max(0, durationMinutes / 60); // Convert to hours
  }
  
  if (selectedSlotType === 'day' && selectedDate && selectedEndDate) {
    const startDate = new Date(selectedDate);
    const endDate = new Date(selectedEndDate);
    const diffTime = endDate.getTime() - startDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
    return Math.max(1, diffDays);
  }
  
  if (selectedSlotType === 'month' && selectedDate && selectedEndDate) {
    const startDate = new Date(selectedDate);
    const endDate = new Date(selectedEndDate);
    const diffMonths = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
    return Math.max(1, diffMonths);
  }
  
  return 1; // Default to 1 unit
}

function getTotalPrice(
  type: StationType | undefined,
  selectedSlotType: string,
  selectedDate: string,
  selectedEndDate: string,
  selectedStartTime: string,
  selectedEndTime: string,
  coupon: Coupon | null = null,
  occurrenceCount = 1
): { unitPrice: number; duration: number; total: number; unitText: string; originalTotal: number; discount: number } | null {
  if (!type) return null;
  
  let unitPrice = 0;
  let unitText = '';
  let total = 0;
  
  const duration = calculateDuration(selectedSlotType, selectedDate, selectedEndDate, selectedStartTime, selectedEndTime);
  
  // Day pricing: number of days * pricePerDay
  if (selectedSlotType === 'day' && type.pricePerDay) {
    unitPrice = type.pricePerDay;
    unitText = 'ליום';
    total = duration * type.pricePerDay;
  }
  // Month pricing: number of months * pricePerMonth
  else if (selectedSlotType === 'month' && type.pricePerMonth) {
    unitPrice = type.pricePerMonth;
    unitText = 'לחודש';
    total = duration * type.pricePerMonth;
  }
  // Hourly pricing with base hours
  else if (selectedSlotType === 'hour' && type.pricePerBaseHour) {
    if (type.extraHourPrice && type.baseHours) {
      // Tiered pricing: ((number of hours - baseHours) * extraHourPrice) + pricePerBaseHour
      const extraHours = Math.max(0, duration - type.baseHours);
      total = type.pricePerBaseHour + (extraHours * type.extraHourPrice);
      
      if (extraHours > 0) {
        unitText = `${type.baseHours} שעות בסיס + ${extraHours.toFixed(1)} שעות נוספות`;
      } else {
        unitText = `עד ${type.baseHours} שעות`;
      }
      unitPrice = total / duration; // Average price per hour for display
    } else {
      // Simple hourly pricing: pricePerBaseHour * number of hours
      unitPrice = type.pricePerBaseHour;
      unitText = 'לשעה';
      total = type.pricePerBaseHour * duration;
    }
  } else {
    return null;
  }

  // Recurring series: same price per occurrence
  const count = Math.max(1, occurrenceCount);
  total = total * count;
  
  const originalTotal = total;
  let discount = 0;
  
  // Apply coupon discount if available
  if (coupon) {
    if (coupon.type === 'percentage') {
      discount = (total * coupon.discount) / 100;
      total = total - discount;
    } else if (coupon.type === 'fixed') {
      discount = coupon.discount;
      total = Math.max(0, total - discount);
    }
  }
  
  return { unitPrice, duration, total, unitText, originalTotal, discount };
}

const BookingDialog: React.FC<BookingDialogProps> = ({
  isOpen,
  selectedStation,
  selectedType,
  selectedSlotType,
  selectedDate,
  selectedEndDate,
  selectedStartTime,
  selectedEndTime,
  seriesDates = [],
  skippedConflictDates = [],
  userEmail,
  userName,
  payLoading,
  payError,
  paySuccess,
  onClose,
  onPay,
  isAdmin = false,
}) => {
  const regulationsEnforced = !isAdmin;
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [regulationsAccepted, setRegulationsAccepted] = useState(false);
  const [hasViewedPdf, setHasViewedPdf] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState<Coupon[]>([]);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [couponProof, setCouponProof] = useState<File | null>(null);
  const [showCouponCode, setShowCouponCode] = useState(false);
  const [showAllSeriesDates, setShowAllSeriesDates] = useState(false);

  const isSeries = seriesDates.length > 0;
  const priceDate = isSeries ? seriesDates[0] : selectedDate;
  const priceEndDate = isSeries ? seriesDates[0] : selectedEndDate;
  const occurrenceCount = isSeries ? seriesDates.length : 1;
  const seriesPreview = showAllSeriesDates ? seriesDates : seriesDates.slice(0, 6);

  // Fetch coupons when dialog opens
  useEffect(() => {
    if (isOpen) {
      getAllCoupons().then(coupons => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Filter coupons that are not expired
        const validCoupons = coupons.filter(coupon => {
          const expirationDate = new Date(coupon.expirationDate);
          expirationDate.setHours(23, 59, 59, 999);
          return expirationDate >= today;
        });
        
        setAvailableCoupons(validCoupons);
      });
    } else {
      // Reset coupon state when dialog closes
      setSelectedCoupon(null);
      setCouponProof(null);
      setShowCouponCode(false);
      setShowAllSeriesDates(false);
    }
  }, [isOpen]);
  
  if (!isOpen || !selectedStation || !selectedType) return null;

  const isHour = selectedSlotType === 'hour';

  const handleOpenPdf = () => {
    setPdfDialogOpen(true);
    // Don't enable checkbox immediately - wait for viewing completion callback
  };

  const handleClosePdf = () => {
    setPdfDialogOpen(false);
  };

  const handleRegulationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRegulationsAccepted(event.target.checked);
  };

  const handlePdfViewingCompleted = () => {
    setHasViewedPdf(true);
  };


  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ direction: 'rtl' }}
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        פרטי הזמנה
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body1">
            <strong>שם:</strong> {userName}
          </Typography>
          <Typography variant="body1">
            <strong>מייל:</strong> {userEmail}
          </Typography>
          <TextField
            label="מספר טלפון"
            value={phoneNumber}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              setPhoneNumber(value);
            }}
            required
            fullWidth
            variant="filled"
            error={!phoneNumber.trim()}
            helperText={!phoneNumber.trim() ? 'שדה חובה' : ''}
            InputLabelProps={{
              sx: {
                right: 12,
                left: 'auto',
                transformOrigin: 'top right',
              }
            }}
            sx={{ 
              direction: 'rtl',
              '& .MuiInputBase-input': {
                textAlign: 'right',
              },
            }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
          <CouponSelection 
            availableCoupons={availableCoupons}
            selectedCoupon={selectedCoupon}
            setSelectedCoupon={setSelectedCoupon}
            couponProof={couponProof}
            setCouponProof={setCouponProof}
            showCouponCode={showCouponCode}
            setShowCouponCode={setShowCouponCode}
          />
          <Typography variant="body1">
            <strong>מספר עמדה:</strong> {selectedStation.id}
          </Typography>
          {isSeries ? (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>סדרה חוזרת:</strong> {seriesDates.length} מועדים
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ direction: 'ltr', textAlign: 'right' }}>
                {seriesPreview.join(' · ')}
                {seriesDates.length > 6 && !showAllSeriesDates ? '…' : ''}
              </Typography>
              {seriesDates.length > 6 && (
                <Button size="small" onClick={() => setShowAllSeriesDates((v) => !v)} sx={{ mt: 0.5 }}>
                  {showAllSeriesDates ? 'הצג פחות' : 'הצג הכל'}
                </Button>
              )}
              {skippedConflictDates.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {skippedConflictDates.length} מועדים תפוסים ידולגו
                  </Typography>
                  <Typography variant="body2" sx={{ direction: 'ltr', textAlign: 'right' }}>
                    {skippedConflictDates.join(' · ')}
                  </Typography>
                </Alert>
              )}
            </Box>
          ) : (
            <Typography variant="body1">
              <strong>תאריך:</strong> {selectedDate}{selectedEndDate ? ` - ${selectedEndDate}` : ''}
            </Typography>
          )}
          {isHour && (
            <Typography variant="body1">
              <strong>שעה:</strong> {selectedStartTime} - {selectedEndTime}
              {isSeries ? ' (לכל מועד)' : ''}
            </Typography>
          )}
          {(() => {
            // Determine if coupon is active
            const isCouponActive = selectedCoupon && (
              !selectedCoupon.needProof || 
              (selectedCoupon.needProof && couponProof !== null)
            );
            const activeCoupon = isCouponActive ? selectedCoupon : null;
            
            const priceInfo = getTotalPrice(
              selectedType,
              selectedSlotType,
              priceDate,
              priceEndDate,
              selectedStartTime,
              selectedEndTime,
              activeCoupon,
              occurrenceCount
            );
            if (!priceInfo) {
              return (
                <Typography variant="body1">
                  <strong>מחיר:</strong> לא זמין
                </Typography>
              );
            }
            
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {isSeries && (
                  <Typography variant="body2" color="text.secondary">
                    מחיר למועד × {occurrenceCount} מועדים
                  </Typography>
                )}
                {selectedSlotType === 'hour' && selectedType?.baseHours && selectedType?.pricePerBaseHour && selectedType?.extraHourPrice ? (
                  // Tiered pricing display
                  <>
                    <Typography variant="body1">
                      <strong>תעריף:</strong> {priceInfo.unitText}
                    </Typography>
                    <Typography variant="body1">
                      <strong>משך זמן למועד:</strong> {priceInfo.duration % 1 === 0 ? priceInfo.duration : priceInfo.duration.toFixed(1)} שעות
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      (חבילת בסיס: {selectedType.pricePerBaseHour}₪
                      {priceInfo.duration > selectedType.baseHours && (
                        <> + {(priceInfo.duration - selectedType.baseHours).toFixed(1)} שעות נוספות ב-{selectedType.extraHourPrice}₪ לשעה</>
                      )}
                      )
                    </Typography>
                  </>
                ) : (
                  // Standard pricing display
                  <>
                    {selectedSlotType === 'hour' ? (
                      <>
                        <Typography variant="body1">
                          <strong>מחיר לשעה:</strong> {selectedType?.pricePerBaseHour} ₪
                        </Typography>
                        <Typography variant="body1">
                          <strong>משך זמן למועד:</strong> {priceInfo.duration % 1 === 0 ? priceInfo.duration : priceInfo.duration.toFixed(1)} שעות
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="body1">
                          <strong>מחיר יחידה:</strong> {priceInfo.unitPrice} ₪ {priceInfo.unitText}
                        </Typography>
                        {!isSeries && (
                          <Typography variant="body1">
                            <strong>כמות:</strong> {
                              selectedSlotType === 'day' 
                                ? `${priceInfo.duration} ימים`
                                : `${priceInfo.duration} חודשים`
                            }
                          </Typography>
                        )}
                      </>
                    )}
                  </>
                )}
                {priceInfo.discount > 0 ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                      מחיר מקורי: {priceInfo.originalTotal % 1 === 0 ? priceInfo.originalTotal : priceInfo.originalTotal.toFixed(2)} ₪
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      הנחה: -{priceInfo.discount % 1 === 0 ? priceInfo.discount : priceInfo.discount.toFixed(2)} ₪
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      <strong>סה"כ לתשלום: {priceInfo.total % 1 === 0 ? priceInfo.total : priceInfo.total.toFixed(2)} ₪</strong>
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    <strong>סה"כ לתשלום: {priceInfo.total % 1 === 0 ? priceInfo.total : priceInfo.total.toFixed(2)} ₪</strong>
                  </Typography>
                )}
              </Box>
            );
          })()}
          
          
          
          {/* Regulations requirement alert - shows until accepted (non-admin only) */}
          {regulationsEnforced && !regulationsAccepted && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                נדרש אישור תקנון
              </Typography>
              <Typography variant="body2">
                לפני השלמת ההזמנה, יש לקרוא ולאשר את התקנון בתחתית הטופס
              </Typography>
            </Alert>
          )}
          
          {/* Warning about approval requirement */}
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              שים לב!!
            </Typography>
            <Typography variant="body2">
              לאחר התשלום, ההזמנה טעונה אישור סופי ע"י הנהלת ההאב
            </Typography>
            <Typography variant="body2">
               כשההזמנה תאושר ישלח אליך מייל ל
               <strong> {userEmail} </strong>
            </Typography>
          </Alert>
          
          {/* Regulations Checkbox (non-admin only) */}
          {regulationsEnforced && (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={regulationsAccepted}
                    onChange={handleRegulationsChange}
                    disabled={!hasViewedPdf}
                    color="primary"
                  />
                }
                label={
                  <Box
                    component="span"
                    onClick={(e) => {
                      e.preventDefault(); // Prevent the checkbox from toggling
                      handleOpenPdf();
                    }}
                    sx={{ 
                      cursor: 'pointer',
                      display: 'inline'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      component="span"
                      color={!hasViewedPdf ? 'text.disabled' : 'text.primary'}
                    >
                      אני מאשר/ת את{' '}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      component="span"
                      sx={{ 
                        color: 'primary.main',
                        textDecoration: 'underline',
                        fontWeight: 'bold',
                        '&:hover': {
                          color: 'primary.dark'
                        }
                      }}
                    >
                      התקנון
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'center' }}
              />
              
              {!hasViewedPdf && (
                <Box sx={{ mt: 1, mr: 4 }}>
                  <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.7rem', display: 'block' }}>
                    לחץ על הטקסט כדי לפתוח את התקנון
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          
          {payError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {payError}
            </Alert>
          )}
          {paySuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {paySuccess}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        {/* Helper text when payment is disabled */}
        {((regulationsEnforced && !regulationsAccepted) || !phoneNumber.trim()) && !payLoading && (
          <Typography variant="caption" color="error" sx={{ textAlign: 'center', fontSize: '0.75rem' }}>
            {!phoneNumber.trim() ? 'יש למלא מספר טלפון' : 'יש לאשר את התקנון לפני ביצוע התשלום'}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ minWidth: 100 }}
          >
            סגור
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              onPay(phoneNumber);
            }}
            disabled={payLoading || (regulationsEnforced && !regulationsAccepted) || !phoneNumber.trim()}
            sx={{ minWidth: 100 }}
            startIcon={payLoading ? <CircularProgress size={16} /> : null}
          >
            {payLoading ? 'מעבד...' : (
              isSeries
                ? `אישור ${seriesDates.length} הזמנות`
                : (selectedType && getPaymentLink(selectedType, selectedSlotType) ? 'תשלום' : 'אישור')
            )}
          </Button>
        </Box>
      </DialogActions>

      {regulationsEnforced && (
        <PdfDialog
          open={pdfDialogOpen}
          onClose={handleClosePdf}
          pdfUrl="/takanon_hub.pdf"
          title="תקנון ההזמנות"
          allowDownload={true}
          enforceViewing={true}
          minimumViewingTime={5}
          onViewingCompleted={handlePdfViewingCompleted}
        />
      )}
    </Dialog>
  );
};

export default BookingDialog;
